import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useClickOutside, useJournalService } from '@/hooks/emotion-detector';
import { useLoginForm } from '@/hooks/forms/useLoginForm';
import { useUserState } from '@/hooks/store';

import { messages } from '../../messages';
import {
	MenuButton,
	ProfileMenuContainer as MenuWrapper,
	ProfileButton,
	ProfileMenu as ProfileMenuContainer,
	UserInfo,
	UserName,
} from '../styles';

export default function ProfileMenu() {
	const { formatMessage } = useIntl();
	const user = useUserState();
	const journalService = useJournalService();
	const { onLogout } = useLoginForm();
	const [showMenu, setShowMenu] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);

	useClickOutside(showMenu, () => setShowMenu(false), menuRef, buttonRef);

	const handleDeleteData = async () => {
		if (isDeleting) return;

		const confirmed = window.confirm(
			'Are you sure you want to delete all your journal entries? This action cannot be undone.',
		);

		if (!confirmed) return;

		setIsDeleting(true);
		try {
			await journalService.deleteJournal();
			setShowMenu(false);
			// Show success message briefly before logging out
			alert('Your data has been deleted successfully. You will now be logged out.');
			onLogout();
		} catch (error) {
			alert('Failed to delete data. Please try again.');
		} finally {
			setIsDeleting(false);
		}
	};

	const handleSignOut = () => {
		setShowMenu(false);
		onLogout();
	};

	return (
		<MenuWrapper>
			<ProfileButton
				ref={buttonRef}
				onClick={() => setShowMenu(!showMenu)}
				$showMenu={showMenu}
				aria-label="Profile menu"
			>
				👤
			</ProfileButton>

			{showMenu && (
				<ProfileMenuContainer ref={menuRef}>
					{user && (
						<UserInfo>
							<UserName>{user.name || user.email}</UserName>
						</UserInfo>
					)}

					<MenuButton onClick={handleDeleteData} disabled={isDeleting}>
						{isDeleting ? 'Deleting...' : formatMessage(messages.deleteData)}
					</MenuButton>

					<MenuButton onClick={handleSignOut}>{formatMessage(messages.signOut)}</MenuButton>
				</ProfileMenuContainer>
			)}
		</MenuWrapper>
	);
}
